import { Box, Button, Flex, HStack, SimpleGrid, Text, Heading } from "@chakra-ui/react";
import BoxHeader from "components/elements/BoxHeader";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { PropertyType, PropertyField } from "types/property";
import { formatFieldTypeValue } from "utils/helpers";
import { useCustomizations } from "hooks/customizations/useCustomizations";

interface PropertyViewProps {
  property: PropertyType | null;
  propertyDetailFields: PropertyField[];
  onEditClick: () => void;
}

function getRowOfCell(cellIndex: number, columns: number) {
  var rowIndex = Math.floor(cellIndex / columns);
  return rowIndex;
}

export function isEvenRow(cellIndex: number, columns: number) {
  const row = getRowOfCell(cellIndex, columns);
  return row === 0 ? true : row % 2 === 0;
}

export function PropertyFieldsView({
  property,
  propertyDetailFields,
  onEditClick,
}: PropertyViewProps) {
  const { user } = useTenant();
  const { isAmplend } = useCustomizations();

  const formatValue = (type: string, value: any) => {
    if (type === "boolean") {
      return value === true || value === "true" ? "Yes" : "No";
    }
    if (type === "percentage") {
      return `${(value * 100).toFixed(2)}%`;
    }
    if (type === "group") return "";
    return formatFieldTypeValue(type, value, user);
  };

  const renderIndividualUnitInfo = () => {
    if (
      !property ||
      propertyDetailFields.find(field => field.data_key === "sub_type")?.values[0]?.value?.field !==
        "Flat"
    ) {
      return null;
    }

    const numberOfUnits = parseInt(
      propertyDetailFields.find(field => field.data_key === "number_of_units")?.values[0]?.value
        ?.field || "0"
    );
    const unitInfoField = propertyDetailFields.find(
      field => field.data_key === "individual_unit_information"
    );

    if (!unitInfoField || !unitInfoField.values) return null;

    return Array.from({ length: numberOfUnits }, (_, index) => {
      const unitIndex = index + 1;
      const unitData = unitInfoField.values[index]?.value || {};
      return (
        <Box key={`unit-${unitIndex}`} mt={6}>
          <Heading size="md" mb={4}>
            Unit {unitIndex}
          </Heading>
          <SimpleGrid columns={2} spacingX="20px">
            {unitInfoField.fields &&
              unitInfoField.fields.map(field => (
                <HStack
                  key={`${field.data_key}_${unitIndex}_${field.id}`}
                  w="100%"
                  bg={isEvenRow(index, 2) ? "#F9F8F8" : "white"}
                  px="12px"
                  alignItems="center"
                  justifyContent="space-between"
                  pl="13px"
                  py="15px"
                  fontWeight="500"
                  fontSize="14px"
                  color="black"
                  borderRadius="6px"
                >
                  <Text>{field.label}</Text>
                  <Text
                    textAlign="right"
                    fontWeight="400"
                    fontSize="16px"
                    lineHeight="28px"
                    color="secondary"
                  >
                    {formatValue(field.type, (unitData as any)[field.data_key]?.field)}
                  </Text>
                </HStack>
              ))}
          </SimpleGrid>
        </Box>
      );
    });
  };

  const renderAmplendIndividualUnitInfo = () => {
    if (
      !property ||
      (!isAmplend &&
        propertyDetailFields.find(field => field.data_key === "sub_type")?.values[0]?.value
          ?.field !== "Flat")
    ) {
      return null;
    }

    const unitInfoField = propertyDetailFields.find(
      field => field.data_key === "individual_unit_information"
    );
    if (!unitInfoField || !unitInfoField.values) return null;

    return unitInfoField.values.map((unitValue, index) => {
      const unitData = unitValue.value || {};
      return (
        <Box key={`unit-${index + 1}`} mt={6}>
          <Heading size="md" mb={4}>
            Unit {index + 1}
          </Heading>
          <SimpleGrid columns={2} spacingX="20px">
            {unitInfoField.fields &&
              unitInfoField.fields.map(field => (
                <HStack
                  key={`${field.data_key}_${index}`}
                  w="100%"
                  bg={isEvenRow(index, 2) ? "#F9F8F8" : "white"}
                  px="12px"
                  alignItems="center"
                  justifyContent="space-between"
                  pl="13px"
                  py="15px"
                  fontWeight="500"
                  fontSize="14px"
                  color="black"
                  borderRadius="6px"
                >
                  <Text>{field.label}</Text>
                  <Text
                    textAlign="right"
                    fontWeight="400"
                    fontSize="16px"
                    lineHeight="28px"
                    color="secondary"
                  >
                    {typeof (unitData as any)[field.data_key]?.field === "object"
                      ? JSON.stringify((unitData as any)[field.data_key]?.field)
                      : formatValue(field.type, (unitData as any)[field.data_key]?.field)}
                  </Text>
                </HStack>
              ))}
          </SimpleGrid>
        </Box>
      );
    });
  };

  const renderSections = () => {
    const sections = propertyDetailFields.reduce((acc, field) => {
      if (!field.section) return acc;
      const section = field.section || "General";
      if (!acc[section]) acc[section] = [];
      acc[section].push(field);
      return acc;
    }, {} as Record<string, PropertyField[]>);

    // Filter out the Property Economics section
    const filteredSections = Object.entries(sections).filter(
      ([sectionName]) => sectionName !== "Property Economics"
    );

    return filteredSections.map(([section, fields], sectionIndex) => (
      <Box key={sectionIndex} mt={sectionIndex === 0 ? 0 : 6}>
        <Flex alignItems="center" justifyContent="space-between" mb={4}>
          <BoxHeader title={`${section}`} />
          {sectionIndex === 0 && (
            <Flex justifyContent="flex-end">
              <Button onClick={onEditClick} variant="white" size="sm">
                Edit Details
              </Button>
            </Flex>
          )}
        </Flex>
        <SimpleGrid columns={2} spacingX="20px">
          {fields.map((field, index) => (
            <HStack
              key={`${field.id}_${index}`}
              w="100%"
              bg={isEvenRow(index, 2) ? "#F9F8F8" : "white"}
              px="12px"
              alignItems="center"
              justifyContent="space-between"
              pl="13px"
              py="15px"
              fontWeight="500"
              fontSize="14px"
              color="black"
              borderRadius="6px"
            >
              <Text>{field.label}</Text>
              <Text
                textAlign="right"
                fontWeight="400"
                fontSize="16px"
                lineHeight="28px"
                color="secondary"
              >
                {formatValue(field.type, field.values[0]?.value?.field)}
              </Text>
            </HStack>
          ))}
        </SimpleGrid>
      </Box>
    ));
  };

  const hasSections = propertyDetailFields.some(field => field.section);

  return (
    <Box>
      {!hasSections && (
        <Flex alignItems="center" justifyContent="space-between" mb="20px">
          <BoxHeader mb="0px" title="Property Details" />
          <Flex justifyContent="flex-end">
            <Button
              data-cy="edit-loan-btn"
              variant="white"
              height="35px"
              borderRadius="7px"
              fontSize="14px"
              fontWeight="500"
              color="gray.200"
              onClick={onEditClick}
            >
              Edit Details
            </Button>
          </Flex>
        </Flex>
      )}

      {hasSections ? (
        renderSections()
      ) : (
        <SimpleGrid columns={2} spacingX="20px">
          {propertyDetailFields
            .filter(field => typeof field.id !== "string" || !field.id.startsWith("unit_"))
            .sort((a: any, b: any) => a.order - b.order)
            .map((field: any, index: number) => {
              return (
                <HStack
                  key={`${field.id}-${index}`}
                  w="100%"
                  bg={isEvenRow(index, 2) ? "#F9F8F8" : "white"}
                  px="12px"
                  alignItems="center"
                  justifyContent="space-between"
                  pl="13px"
                  py="15px"
                  fontWeight="500"
                  fontSize="14px"
                  color="black"
                  borderRadius="6px"
                >
                  <Text>{field.label}</Text>
                  <Text
                    textAlign="right"
                    fontWeight="400"
                    fontSize="16px"
                    lineHeight="28px"
                    color="secondary"
                  >
                    {formatValue(field.type, field.values[0]?.value?.field)}
                  </Text>
                </HStack>
              );
            })}
        </SimpleGrid>
      )}

      {isAmplend ? renderAmplendIndividualUnitInfo() : renderIndividualUnitInfo()}
    </Box>
  );
}
